import Vue from 'vue'
import { isEmpty } from 'lodash-es'
import { useEventBus } from '@vueuse/core'
import parsePushData from 'custom-push/pushParser/parsePushData'
import parseTopicInfo from 'custom-push/pushParser/parseTopicInfo'
import { isLiveMatch, isPrematch } from '@ms/components/match/status.js'

/**
 * 更新 market status
 * @param {*} markets {
 *  id: market,
 *  id: [market] 带specifiers
 * }
 */
export function updateMarketStatus(markets, status) {
  if (status !== 1 && status !== 2) {
    return
  }
  if (!isEmpty(markets)) {
    markets.order.forEach((marketId) => {
      const m = markets.map[marketId]
      if (Array.isArray(m)) {
        m.forEach((market) => {
          market.status = status
        })
      } else {
        m.status = status
      }
    })
  }
}

/**
 * 根据推送消息，更新对应 event 信息
 * @param  {Object}  eventsMap    比赛列表经转换后的 map 对象
 * @param  {Array}  eventsOrder   比赛列表经转换后的 order 数组
 * @param  {Array}  pushData      推送消息对象
 * @param  {Boolean} isInsert     是否新增
 * @param  {Boolean} isDel        是否删除
 * @return {[type]}               [description]
 */
export const updateEvent = (eventsMap, eventsOrder, pushData, isInsert = true, isDel = true) => {
  const eventInfo = parsePushData(pushData, 'event')
  const topicInfo = parseTopicInfo(pushData.topic, 'event')

  if (!eventInfo) {
    return
  }
  const { eventId, tournamentId } = topicInfo
  const categoryId = `sr:category:${topicInfo.categoryId}`
  eventInfo.marketsCount = eventInfo.bmLcooCount
  delete eventInfo.bmLcooCount
  let event
  if (eventsMap && eventsOrder) {
    const status = eventInfo.status === 0 || eventInfo.status === 1 || eventInfo.status === 2 || eventInfo.status === 7
    event = eventsMap[eventId]
    if (event) {
      // pushTime直接合并上去，不受监控
      if ((event.pushTime && event.pushTime < eventInfo.pushTime) || !event.pushTime) {
        // 比赛不可以投注删除
        if (!status && isDel) {
          Vue.delete(eventsMap, eventId)
          // 如果 eventsOrder 是 eventId 组成的数组，则直接处理
          if (Array.isArray(eventsOrder)) {
            const index = eventsOrder.findIndex((cur) => cur === eventId)
            if (index > -1) {
              eventsOrder.splice(index, 1)
            }
          } else {
            for (const key in eventsOrder) {
              const one = eventsOrder[key]
              const index = one.findIndex((cur) => cur === eventId)
              if (index > -1) {
                one.splice(index, 1)
                if (one.length === 0) {
                  Vue.delete(eventsOrder, key)
                }
                break
              }
            }
          }
        } else {
          // 更新比赛信息
          const isPrematchMatchOfOld = isPrematch(event.status)
          const isLiveMatchOfNew = isLiveMatch(eventInfo.status)
          const betStatus = eventInfo.betStatus
          delete eventInfo.betStatus
          Object.assign(event, eventInfo)
          if (betStatus && (betStatus === 1 || betStatus === 2)) {
            updateMarketStatus(event.markets, betStatus)
          }

          if (isPrematchMatchOfOld && isLiveMatchOfNew) {
            console.log('-------------------> ON_MATCH_CHANGED_TO_LIVE', event.eventId)
            useEventBus('ON_MATCH_CHANGED_TO_LIVE').emit({
              eventId: event.eventId,
              sportId: event.sportId
            })
          }
        }
      }
      // 只有在eventInfo是 1， 2，8的情况下才能新增比赛
    } else if (eventInfo.startTime && status && isInsert) {
      // 如果  marketsCount 不是大于0的也不显示
      if (!(eventInfo.marketsCount > 0)) {
        return
      }
      Object.assign(eventInfo, {
        categoryId,
        tournamentId,
        eventId
      })
      Vue.set(eventsMap, eventId, eventInfo)
      const time = eventInfo.startTime
      if (time && !Array.isArray(eventsOrder)) {
        if (!eventsOrder[time]) {
          Vue.set(eventsOrder, time, [])
        }
        eventsOrder[time].push(eventId)
      }
    }
  }
}

/**
 * 根据推送消息，更新对应 event market 信息
 * @param  {Object} event    显示的 event 比赛对象，注意其中的 markets 信息有做 convert
 * @param  {Object} pushData 推送消息对象
 * @return {[type]}          [description]
 */
export const updateMarket = (event, pushData) => {
  const pushMarketInfo = parsePushData(pushData, 'market')
  const topicInfo = parseTopicInfo(pushData[0], 'market')

  const { marketId } = topicInfo

  let specifiers = topicInfo.marketSpecifiers
  if (specifiers) {
    // topic中不能出现 |所以specifier多个的情况用&连接，现在在改回去
    specifiers = specifiers.split('&').join('|')
  }

  // 新的market
  let market = { id: marketId }
  if (specifiers) {
    market.specifiers = specifiers
  }

  if (!event.markets || !event.markets.map) {
    event.markets = { map: {}, order: [] }
  }

  const originMarket = event.markets.map[marketId]

  const oldTime = originMarket ? +originMarket.pushTime || 0 : 0

  // 原始market存在
  if (originMarket) {
    // 如果已经是 live 了，就不合并推送的 prematch 信息
    if (+pushMarketInfo.product === 3 && originMarket.product === 1) {
      return
    }

    market = {
      ...market,
      ...pushMarketInfo
    }

    if (specifiers) {
      if (Array.isArray(originMarket)) {
        const i = originMarket.findIndex((cur) => cur.specifiers === specifiers)

        if (i > -1) {
          market.outcomes = originMarket[i].outcomes
          originMarket.splice(i, 1, market)
        } else {
          originMarket.push(market)
        }
      } else {
        Object.assign(originMarket, pushMarketInfo)
        market = originMarket
      }
    } else {
      Object.assign(originMarket, pushMarketInfo)
      market = originMarket
    }
  } else if (oldTime < +pushMarketInfo.pushTime) {
    // 新增 market
    event.markets.order.push(marketId)
    market = { ...market, ...pushMarketInfo }

    if (specifiers) {
      Vue.set(event.markets.map, marketId, [market])
    } else {
      Vue.set(event.markets.map, marketId, market)
    }
  }

  return { market, oldTime, pushMarketInfo }
}

/**
 * 根据推送消息，更新对应 event outcome 信息
 * @param  {Object} event    显示的 event 比赛对象，注意其中的 markets 信息有做 convert
 * @param  {Object} pushData 推送消息对象
 * @return {[type]}          [description]
 */
export const updateOutcome = (event, pushData) => {
  const pushOutcomeInfos = parsePushData(pushData, 'odds') || {}
  // 修正 odds 推送中的 market priority 位置
  const marketPushData = [...pushData]
  marketPushData[9] = marketPushData[10]
  const marketInfo = updateMarket(event, marketPushData)
  if (!marketInfo) return
  const { market, oldTime, pushMarketInfo } = marketInfo

  if (pushOutcomeInfos && oldTime <= +pushMarketInfo.pushTime) {
    let outcomes = market.outcomes
    if (!outcomes) {
      outcomes = { map: {}, order: [] }
      Vue.set(market, 'outcomes', outcomes)
    }

    Object.entries(pushOutcomeInfos).forEach(([id, outcome]) => {
      outcome.id = id
      if (outcomes.map[id]) {
        Object.assign(outcomes.map[id], outcome)
      } else {
        Vue.set(market.outcomes.map, id, outcome)
        market.outcomes.order.push(id)
      }
    })
  }
}
